import { isEqual } from '@luminovo/commons';
import { useSearch, useSetSearch } from '@luminovo/design-system';
import * as React from 'react';
import { z } from 'zod/v4';

/**
 * The query params, each JSON, a `TanStackTable` keeps its filters, sorting and search in; every table on a page
 * shares them. Spread them into a route's `validateSearch`: the app does so in its signed-in layout, for every page.
 */
export const tableSearchParams = {
    columnFilters: z.string().optional(),
    sorting: z.string().optional(),
    globalFilter: z.string().optional(),
};

type TableSearchKey = keyof typeof tableSearchParams;

type Options = {
    /** Whether the state lives in the URL. Without, it lives in the component, as with `React.useState`. */
    enabled: boolean;
    /** Whether a value read from the URL has the right shape; one that hasn't reads as `fallback`. Keep it stable. */
    isValid: (value: unknown) => boolean;
};

/**
 * Table state in the query param `key` while `enabled`: the URL's value, or `fallback` while the URL holds none. The
 * table follows the URL, so Back, Forward and a link without the param (its navbar entry) show what that URL says.
 *
 * The setter takes a value or an updater, as TanStack Table's handlers pass it. Its write replaces the history entry,
 * keeps the scroll and skips the unsaved-changes guard, since a table's view isn't the user's work. A value equal to
 * `fallback` removes the param.
 */
export function useTableSearchState<T>(
    key: TableSearchKey,
    fallback: T,
    { enabled, isValid }: Options,
): [T, (input: T | ((value: T) => T)) => void] {
    const param = useSearch({ strict: false, select: (search) => search[key] });
    const setSearch = useSetSearch();
    const fromUrl = React.useMemo(
        () => (enabled ? parseParam<T>(param, isValid) : undefined),
        [enabled, param, isValid],
    );
    const [local, setLocal] = React.useState(fallback);
    const value = enabled ? (fromUrl ? fromUrl.value : fallback) : local;

    // The router delivers a write a few microtasks later, so an updater called again before that (Clear all clears
    // column by column) builds on what was written, not on what the URL still says.
    const written = React.useRef({ value, param });
    React.useLayoutEffect(() => {
        written.current = { value, param };
    }, [value, param]);

    const setValue = React.useCallback(
        (input: T | ((value: T) => T)) => {
            if (!enabled) {
                setLocal(input);
                return;
            }
            const next = typeof input === 'function' ? (input as (value: T) => T)(written.current.value) : input;
            const nextParam = isEqual(next, fallback) ? undefined : JSON.stringify(next);
            const unchanged = nextParam === written.current.param;
            written.current = { value: next, param: nextParam };
            if (!unchanged) {
                setSearch({ [key]: nextParam } as Partial<Record<TableSearchKey, string>>, { ignoreBlocker: true });
            }
        },
        [enabled, fallback, key, setSearch],
    );

    return [value, setValue];
}

function parseParam<T>(param: string | undefined, isValid: (value: unknown) => boolean): { value: T } | undefined {
    if (param === undefined) {
        return undefined;
    }
    try {
        const value: unknown = JSON.parse(param);
        return isValid(value) ? { value: value as T } : undefined;
    } catch {
        return undefined;
    }
}
